/**
 * Parse a date-only string (YYYY-MM-DD) as a local date without timezone conversion.
 * This prevents the common bug where dates shift by ±1 day due to UTC interpretation.
 * 
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object in local timezone, or null if invalid
 */
export function parseDateOnly(dateString: string | null | undefined): Date | null {
  if (!dateString) return null
  
  const parts = dateString.split('-')
  if (parts.length !== 3) return null
  
  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10) - 1 // JS months are 0-indexed
  const day = parseInt(parts[2], 10)
  
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null
  
  // Create date in local timezone (not UTC)
  return new Date(year, month, day)
}

/**
 * Format a date-only string (YYYY-MM-DD) for display without timezone conversion.
 * 
 * @param dateString - Date string in YYYY-MM-DD format
 * @param locale - Locale for formatting (default: 'en-GB')
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string
 */
export function formatDateOnly(
  dateString: string | null | undefined,
  locale: string = 'en-GB',
  options?: Intl.DateTimeFormatOptions
): string {
  const date = parseDateOnly(dateString)
  if (!date) return ''
  
  return date.toLocaleDateString(locale, options)
}
